/**
 * Client for the approval path (stage 3, UX_CHAT/PLAN.md AP8/AP9).
 *
 * Exactly one call: the human's decision on a plan. `[Ja]` and `[Nein]` on the confirmation card
 * go HERE — to a route, never to the model. The server checks status, fingerprint, expiry and
 * tenant and executes the plan via the real routes; a second click no longer finds the status in
 * place (409).
 *
 * Like `GraphExplorerAPI`: `authenticatedFetch`, server shapes, no conversion.
 */
import { configuration } from './Configuration';
import { ApplicationError } from './general/Errors';
import { authenticatedFetch } from './JWTAuthService';

export type ApprovalDecision = 'confirm' | 'confirmPublish' | 'cancel';

export interface ApprovalStepResult {
    path: string;
    className: string;
    outcome: 'created' | 'linked' | 'skipped' | 'failed';
    id?: string;
    label?: string;
    reason?: string;
    /** Portal route to the created object (a prompt tool: its editor). */
    route?: string;
}

export interface ApprovalResult {
    planId: string;
    status: 'done' | 'cancelled' | 'partial' | 'rejected';
    results: ApprovalStepResult[];
    message?: string;
}

export const decideApproval = async (planId: string, decision: ApprovalDecision): Promise<ApprovalResult> => {
    const response = await authenticatedFetch(
        `${configuration.serverUrl}/approvals/${encodeURIComponent(planId)}/decision`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ decision }),
        },
    );
    let body: any = null;
    try {
        body = await response.json();
    } catch {
        body = null;
    }
    if (response.status === 409) {
        return { planId, status: 'rejected', results: [], message: body?.error ?? 'already decided' };
    }
    if (response.status === 404) {
        return { planId, status: 'rejected', results: [], message: body?.error ?? 'not found' };
    }
    if (!response.ok) {
        throw new ApplicationError(body?.error ?? `approval failed (${response.status})`);
    }
    return {
        planId,
        status: body?.status ?? 'done',
        results: Array.isArray(body?.results) ? body.results : [],
        ...(body?.message ? { message: body.message } : {}),
    };
};
